import React, { useEffect, useState, useMemo } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import NoteTable from '../../../components/tables/NoteTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useNoteApi } from '../../../hooks/useNoteApi';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import {
  ComponentDTO,
  NoteDTO,
  NoteTypeDTO,
  UserDTO,
} from '../../../services/apiClient';
import { useNavigate, useLocation } from 'react-router-dom';
import { useUserApi } from '../../../hooks/useUserApi';
import { useComponents } from '../../../hooks/useComponentApi';
import { useNoteTypeApi } from '../../../hooks/useNoteTypeApi';
import { NoteTypeConstants } from '../../../constants/NoteTypeConstants';
import { getUserId } from '../../../utils/tokenUtils';

// View configuration
interface ViewConfig {
  title: string;
  breadcrumb: string;
  activeMenuItem: string;
  noteTypeFilter?: string[];
  assignedToCurrentAdmin?: boolean;
}

const NoteList: React.FC = () => {
  const { search, remove, extract, resolve, isLoading, error } = useNoteApi();
  const [items, setItems] = useState<NoteDTO[]>([]);
  const [admins, setAdmins] = useState<UserDTO[]>([]);
  const [components, setComponents] = useState<ComponentDTO[]>([]);
  const [noteTypes, setNoteTypes] = useState<NoteTypeDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});

  const navigate = useNavigate();
  const location = useLocation();
  const userId = getUserId(); // Logged-in admin's user ID
  const { search: searchAdmins, isLoading: isLoadingAdmins } = useUserApi();
  const { data: componentsData, isLoading: isLoadingComponents } = useComponents();
  const { search: searchNoteTypes, isLoading: isLoadingNoteTypes } = useNoteTypeApi();

  // Determine view config based on route
  const viewConfig: ViewConfig = useMemo(() => {
    const path = location.pathname;

    // Tasks - show TODO_TASK + SYSTEM_TASK
    if (path.includes('/tasks')) {
      return {
        title: 'Manage Tasks',
        breadcrumb: 'Tasks',
        activeMenuItem: '/admin/tasks',
        noteTypeFilter: [NoteTypeConstants.TODO_TASK, NoteTypeConstants.SYSTEM_TASK],
      };
    }

    // My Tasks - show TODO_TASK + SYSTEM_TASK assigned to current admin
    if (path.includes('/my-tasks')) {
      return {
        title: 'My Tasks',
        breadcrumb: 'My Tasks',
        activeMenuItem: '/admin/my-tasks',
        noteTypeFilter: [NoteTypeConstants.TODO_TASK, NoteTypeConstants.SYSTEM_TASK],
        assignedToCurrentAdmin: true,
      };
    }

    // Flags - show FLAG type only
    if (path.includes('/flags')) {
      return {
        title: 'Manage Flags',
        breadcrumb: 'Flags',
        activeMenuItem: '/admin/flags',
        noteTypeFilter: [NoteTypeConstants.FLAG],
      };
    }

    // Notes - show ALL notes (no filter)
    return {
      title: 'Manage Notes',
      breadcrumb: 'Notes',
      activeMenuItem: '/admin/notes',
    };
  }, [location.pathname]);

  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Notes / Tasks', href: '#' },
    { label: viewConfig.breadcrumb, href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const baseFilters = { ...filters };

      // Apply assigned admin filter for My Tasks (use logged-in user's ID)
      if (viewConfig.assignedToCurrentAdmin && userId) {
        baseFilters.assignedAdminId = userId;
      }

      // Handle multiple note types (Tasks)
      if (viewConfig.noteTypeFilter && viewConfig.noteTypeFilter.length > 1) {
        const responses = await Promise.all(
          viewConfig.noteTypeFilter.map(noteTypeId =>
            search({
              filters: { ...baseFilters, noteTypeId },
              page,
              pageSize: size,
              sortBy: 'CreatedUtc',
              sortDescending: true,
            })
          )
        );

        const combinedResults = responses.flatMap(r => r?.data?.results || []);
        combinedResults.sort((a, b) => {
          const dateA = new Date(a.createdUtc || 0).getTime();
          const dateB = new Date(b.createdUtc || 0).getTime();
          return dateB - dateA;
        });

        setItems(combinedResults);
        setTotalRecords(responses.reduce((sum, r) => sum + (r?.data?.totalCount || 0), 0));
        return;
      }

      // Handle single note type (Flags) or no filter (Notes)
      if (viewConfig.noteTypeFilter && viewConfig.noteTypeFilter.length === 1) {
        baseFilters.noteTypeId = viewConfig.noteTypeFilter[0];
      }

      const response = await search({
        filters: baseFilters,
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });

      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, searchFilters);
  }, [currentPage, pageSize, location.pathname]);

  useEffect(() => {
    setCurrentPage(1);
    setSearchFilters({});
  }, [location.pathname]);

  useEffect(() => {
    const fetchAdmins = async () => {
      try {
        const response = await searchAdmins({
          filters: {
            role: 'Admin',
          },
          page: 1,
          pageSize: 999,
          sortBy: 'FirstName',
        });
        setAdmins(response.data?.results || []);
      } catch (error) {
        console.log('Error fetching admins: ', error);
      }
    };

    const fetchNoteTypes = async () => {
      try {
        const response = await searchNoteTypes({
          page: 1,
          pageSize: 999,
          sortBy: 'DisplayPosition',
        });
        setNoteTypes(response?.data?.results || []);
      } catch (error) {
        console.log('Error fetching note types: ', error);
      }
    };

    fetchAdmins();
    fetchNoteTypes();
  }, []);

  useEffect(() => {
    setComponents(componentsData || []);
  }, [componentsData]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  const handleDelete = async (dto: NoteDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await fetchItems(currentPage, pageSize, searchFilters);
    } catch (error) {
      console.error('Error deleting Note:', error);
    }
  };

  const handleResolve = async (dto: NoteDTO) => {
    try {
      await resolve(dto.id || '');
      await fetchItems(currentPage, pageSize, searchFilters);
    } catch (error) {
      console.error('Error resolving Note:', error);
    }
  };

  const handleViewMember = async (dto: NoteDTO) => {
    navigate(`/admin/members/${dto.memberId}`);
  };

  const handleImpersonateMemberLogin = async (dto: NoteDTO): Promise<void> => {
    if (!dto.member?.userID) {
      console.error('No member user ID found for impersonation');
      return;
    }

    try {
      window.open(`/admin/impersonate/${dto.memberId}`, '_blank');
    } catch (error) {
      console.error('Error opening impersonation tab', error);
    }
  };

  const handleExport = async () => {
    try {
      const response = await extract({
        filters: searchFilters,
        page: 1,
        pageSize: 9999999, // Large number to export all matching records
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });

      if (response.data) {
        window.location.href = response.data;
      }
    } catch (error) {
      console.error('Error exporting Notes:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem={viewConfig.activeMenuItem}
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Main.Note.View"
    >
      <Card
        title={viewConfig.title}
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <div className="flex space-x-2">
            <Button type="button" variant="secondary" onClick={handleExport}>
              Export
            </Button>
          </div>
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            {!viewConfig.assignedToCurrentAdmin && (
              <FormField
                id="assignedAdminId"
                label="Search by Assigned Admin"
                value={searchFilters.assignedAdminId || ''}
                type="dropdown"
                onChange={(e) =>
                  setSearchFilters({ ...searchFilters, assignedAdminId: e.target.value })
                }
                placeholder="Select Assigned Admin"
                options={[
                  ...admins.map((admin) => ({
                    value: admin.id,
                    label: `${admin.firstName} ${admin.lastName}`,
                  })),
                ]}
                isLoading={isLoadingAdmins}
              />
            )}
            <FormField
              id="componentId"
              label="Search by Component"
              value={searchFilters.componentId || ''}
              type="dropdown"
              onChange={(e) => setSearchFilters({ ...searchFilters, componentId: e.target.value })}
              placeholder="Select Component"
              options={[
                ...components.map((component) => ({
                  value: component.id,
                  label: component.title,
                })),
              ]}
              isLoading={isLoadingComponents}
            />
            <FormField
              id="noteTypeId"
              label="Search by Note Type"
              value={searchFilters.noteTypeId || ''}
              type="dropdown"
              onChange={(e) => setSearchFilters({ ...searchFilters, noteTypeId: e.target.value })}
              placeholder="Select Note Type"
              options={[
                ...noteTypes.map((noteType) => ({
                  value: noteType.id,
                  label: noteType.title,
                })),
              ]}
              isLoading={isLoadingNoteTypes}
            />
            <FormField
              id="reference"
              label="Search by Reference"
              value={searchFilters.reference || ''}
              onChange={(e) => setSearchFilters({ ...searchFilters, reference: e.target.value })}
              placeholder="Search by Reference"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <NoteTable
          data={items}
          handleEdit={(dto) => navigate(`/admin/notes/${dto.id}`)}
          handleDelete={handleDelete}
          handleResolve={handleResolve}
          handleViewMember={handleViewMember}
          handleImpersonateMemberLogin={handleImpersonateMemberLogin}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default NoteList;
